import { Component, OnInit } from '@angular/core';
import { GetSemesterDto } from '../../../../_generated/hka-app-service/model/getSemesterDto';
import { IgnoreSemestersService } from '../../service/ignore-semesters.service';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { MatListOption } from '@angular/material/list';
import { ConfirmationDialogService } from '../../../confirmation-dialog/confirmation-dialog.service';

@Component({
  selector: 'app-ignore-semesters',
  templateUrl: './ignore-semesters.component.html',
  styleUrls: ['./ignore-semesters.component.scss'],
})
export class IgnoreSemestersComponent implements OnInit {
  semesters: Observable<GetSemesterDto[]>;
  semesterIndex: Observable<Map<string, GetSemesterDto[]>>;
  faculties: Observable<string[]>;
  selectedFaculty: number = 0;
  currentSelectedFaculty: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  ignoredSemesters: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  constructor(
    private ignoreSemestersService: IgnoreSemestersService,
    private confirmationDialog: ConfirmationDialogService,
  ) {}

  ngOnInit(): void {
    // fetch ignored semesters to retain UI list between reloads/sessions
    this.ignoreSemestersService.getIgnoredSemesters().subscribe((semestersToIgnore) => {
      const semesterNames = semestersToIgnore.map((semester) => semester.name);
      this.ignoredSemesters.next(semesterNames.sort());
    });

    this.loadFaculties();
  }

  setSelectedFaculty(selectChange: number) {
    if (selectChange) {
      // selectChange is actually passed in as the faculty name string, but angular auto defines it as a number in the html
      const selectedFaculty = selectChange as unknown as string;
      this.semesters = this.semesterIndex.pipe(
        map((index) => index.get(selectedFaculty).sort((a, b) => a.name.localeCompare(b.name))),
      );
      this.currentSelectedFaculty.next(selectChange);
    }
  }

  ignoreSemesters(semesterSelection: MatListOption[]) {
    let ignore: string[] = semesterSelection.map((s) => s.value.name);
    this.ignoreSemestersService.ignoreSemesters(ignore);
    this.ignoredSemesters.next([...this.ignoredSemesters.value, ...ignore].sort());

    // reload semester list to remove ignored semesters
    const currentFaculty = this.currentSelectedFaculty.getValue().toString();
    this.semesters = this.semesterIndex.pipe(
      map((index) =>
        index
          .get(currentFaculty)
          .filter((s) => !this.ignoredSemesters.value.includes(s.name))
          .sort((a, b) => a.name.localeCompare(b.name)),
      ),
    );
  }

  unignoreSemesters(semesterSelection: MatListOption[]) {
    let unignore: string[] = semesterSelection.map((s) => s.value);
    this.ignoreSemestersService.unignoreSemesters(unignore).then(() => {
      // reload faculties in case any fully ignored faculty now has available semesters again
      this.loadFaculties();
      this.ignoredSemesters.next(this.ignoredSemesters.value.filter((a) => !unignore.some((b) => a == b)).sort());

      // reload semester list in case current faculty's semesters were unignored
      // adding a check to only execute this when any unignored semester is assigned to the selected faculty still had the UI list refresh and caused the same number of API calls
      // this could probably be further optimized
      const currentFaculty = this.currentSelectedFaculty.getValue().toString();
      this.semesters = this.semesterIndex.pipe(
        map((index) => index.get(currentFaculty).sort((a, b) => a.name.localeCompare(b.name))),
      );
    });
  }

  unignoreAll() {
    this.confirmationDialog
      .askForConfirmation({
        message: 'Sind Sie sicher, dass sie alle Semester von der Liste entfernen möchten?',
        dialogOptions: ['CANCEL', 'CONFIRM'],
      })
      .pipe(filter((result) => result == 'CONFIRM'))
      .subscribe(() => {
        this.ignoreSemestersService.unignoreAll().then(() => {
          // reload faculties in case any fully ignored faculty now has available semesters again
          this.loadFaculties();
          this.ignoredSemesters.next([]);
          this.selectedFaculty = 0;
          this.currentSelectedFaculty.next(0);
        });
      });
  }

  private loadFaculties() {
    // note: if all semesters of a faculty are ignored, the faculty will not be returned by the API and therefore not be shown in the UI
    this.semesterIndex = this.ignoreSemestersService.getAllSemestersByFaculty();
    this.faculties = this.semesterIndex.pipe(map((index) => Array.from(index.keys()).sort()));
  }
}
